/**
 * Ensures a URL uses HTTPS protocol
 * @param {string} url - The URL to normalize
 * @returns {string} - The URL with HTTPS protocol
 */
export function ensureHttps(url) {
  if (!url) return url;
  
  // If it's already a full URL
  if (url.startsWith('http://') || url.startsWith('https://')) {
    // Replace http:// with https://
    return url.replace(/^http:\/\//i, 'https://');
  }
  
  // If it's a relative path or data URL, return as-is
  return url;
}

/**
 * Logs URL information for debugging mixed content issues
 * @param {string} source - Where the URL is being used
 * @param {string} url - The URL being loaded
 */
export function logUrl(source, url) {
  if (url && typeof url === 'string') {
    if (url.startsWith('http://')) {
      console.warn(`[MIXED CONTENT WARNING] ${source} is using HTTP:`, url);
    } else if (url.startsWith('https://')) {
      console.log(`[HTTPS OK] ${source}:`, url);
    } else {
      console.log(`[RELATIVE/DATA URL] ${source}:`, url.substring(0, 50) + '...');
    }
  }
}

/**
 * Scans an object recursively for HTTP URLs and logs them
 * @param {object} obj - The object to scan
 * @param {string} prefix - Prefix for logging (field path)
 * @returns {Array} Array of found HTTP URLs with their field paths
 */
export function scanForHttpUrls(obj, prefix = '') {
  const unsafeUrls = [];
  
  if (!obj || typeof obj !== 'object') {
    return unsafeUrls;
  }
  
  for (const [key, value] of Object.entries(obj)) {
    const fieldPath = prefix ? `${prefix}.${key}` : key;
    
    if (value === null || value === undefined) {
      continue;
    }
    
    // Check if value is a string that starts with http://
    if (typeof value === 'string' && value.trim().toLowerCase().startsWith('http://')) {
      console.warn(`[MixedContent] Unsafe URL found in company profile:`, {
        field: fieldPath,
        value: value,
        fullPath: `companyProfile.${fieldPath}`
      });
      unsafeUrls.push({
        field: fieldPath,
        fullPath: `companyProfile.${fieldPath}`,
        value: value
      });
    }
    
    // Recursively scan nested objects
    if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
      const nestedUrls = scanForHttpUrls(value, fieldPath);
      unsafeUrls.push(...nestedUrls);
    }
    
    // Scan arrays
    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (typeof item === 'object' && item !== null) {
          const arrayUrls = scanForHttpUrls(item, `${fieldPath}[${index}]`);
          unsafeUrls.push(...arrayUrls);
        } else if (typeof item === 'string' && item.trim().toLowerCase().startsWith('http://')) {
          console.warn(`[MixedContent] Unsafe URL found in company profile array:`, {
            field: `${fieldPath}[${index}]`,
            value: item,
            fullPath: `companyProfile.${fieldPath}[${index}]`
          });
          unsafeUrls.push({
            field: `${fieldPath}[${index}]`,
            fullPath: `companyProfile.${fieldPath}[${index}]`,
            value: item
          });
        }
      });
    }
  }
  
  return unsafeUrls;
}

